/**
 * MetricList — Lista compacta de 2–3 ítems para tarjetas KPI.
 *
 * QUÉ HACE: pinta filas de una sola línea con un marcador opcional (⚠️ aviso,
 *   🟢 activo), la etiqueta (truncada), una barra de agotamiento opcional y un
 *   valor opcional alineado a la derecha. La usa "Stock Crítico" (productos con
 *   unidades y su barra de agotamiento). Las filas se reparten el alto libre.
 * QUÉ INVOCA: MetricProgressBar (UI compartida) y `useReducedMotion`.
 * QUÉ EXPORTA: MetricList, MetricListItem.
 * QUÉ NO DEBE HACER: no calcula métricas ni accede a datos.
 */
import type { CSSProperties } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { staggerDelay } from "@/lib/animation/motion";
import { MetricProgressBar } from "./MetricProgressBar";

/** Fila de la lista. */
export interface MetricListItem {
  /** Texto principal (se trunca si no cabe). */
  label: string;
  /** Valor secundario alineado a la derecha ("2 uds", "24%"). */
  value?: string;
  /** Marcador visual al inicio de la fila. */
  marker?: "warning";
  /** Barra de agotamiento en línea (0–100 + color). */
  progress?: { value: number; fillClassName?: string };
}

interface MetricListProps {
  items: MetricListItem[];
  className?: string;
}

const MARKERS: Record<NonNullable<MetricListItem["marker"]>, string> = {
  warning: "⚠️",
};

export const MetricList = ({ items, className = "" }: MetricListProps) => {
  const reducedMotion = useReducedMotion();

  if (items.length === 0) return null;

  return (
    <div
      className={`flex min-h-0 flex-col gap-1.5 overflow-hidden ${className}`}
    >
      {items.map((item, index) => (
        <div
          key={`${item.label}-${index}`}
          title={`${item.label}${item.value ? ` · ${item.value}` : ""}`}
          className={`flex min-w-0 items-center gap-1.5${
            reducedMotion ? "" : " animate-chart-fade"
          }`}
          style={
            reducedMotion
              ? undefined
              : ({ animationDelay: staggerDelay(index) } as CSSProperties)
          }
        >
          {item.marker && (
            <span
              className="shrink-0 text-[9px] leading-none"
              aria-hidden="true"
            >
              {MARKERS[item.marker]}
            </span>
          )}
          <span className="min-w-0 flex-1 truncate text-[8px] font-medium text-gray-700 dark:text-gray-200 sm:text-[9px]">
            {item.label}
          </span>
          {item.progress && (
            <MetricProgressBar
              value={item.progress.value}
              fillClassName={item.progress.fillClassName}
              heightClassName="h-1.5"
              className="w-10 shrink-0 sm:w-14"
            />
          )}
          {item.value && (
            <span className="shrink-0 text-[8px] font-semibold tabular-nums text-gray-500 dark:text-gray-400">
              {item.value}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};
